import React, { useState } from 'react';
import {
  Button,
  Dropdown,
  MultiSelectDropdown, // Toggle,
  useToasts,
} from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  ErrorField,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
} from '../../../../../../../shared/styles';
import { getText } from '../../../../../../../shared/text';
import { useInviteUsers } from '../../hooks';
import { CustomSelect } from './styles';
import { INVITE_ROLES } from '../../constants';

const {
  settings: { user },
} = getText('EN');

type NewUserFormProps = {
  toggleDrawer: any;
  count: number;
  limit: number;
  setSelectedTab: any;
  selectedTab: any;
};

const NewUserForm = (props: NewUserFormProps) => {
  const { t } = useTranslation();
  const { toggleDrawer, count, limit, setSelectedTab, selectedTab } = props;
  // const [weeklyDigest, setWeeklyDigest] = useState<boolean>(true);
  // const [monthlyDigest, setMonthlyDigest] = useState<boolean>(true);
  const [selectedEmail, setSelectedEmail] = useState<any>([]);
  const invite_roles = INVITE_ROLES.map((role) => {
    return { ...role, name: t(`${role.name}`), desc: t(`${role.desc}`) };
  });
  const [, setToast] = useToasts();
  const RemainingCount = limit - count;
  const { mutate: inviteUsers } = useInviteUsers({
    setToast,
    callback: (isSuccess) => {
      if (isSuccess) {
        setSelectedTab('pending');
      }
    },
  });
  // const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const form = useForm({
    resolver: yupResolver(
      Yup.object({
        role: Yup.string().required(`${t('forms.err_msg.role_required')}`),
        emails: Yup.array()
          .required()
          .min(1, `${t('forms.err_msg.email_required')}`)
          .max(
            RemainingCount,
            `${t('forms.err_msg.user_limit', {
              field: `${RemainingCount}`,
            })}`,
          )
          .test({
            message: 'Error',
            name: 'email',
            test: function (email, context) {
              const { createError } = context || {};
              if (email) {
                const errorvalues = email?.map((val: any) => {
                  const item = val.label;
                  const pattrn = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  return !pattrn.test(item);
                });
                if (errorvalues.some((s) => !!s)) {
                  return createError({
                    message: `${t('forms.err_msg.valid_email')}`,
                  });
                }
              }
              return true;
            },
          }),
      }).required(),
    ),
    mode: 'onChange',
    defaultValues: {
      role: invite_roles[0].id,
      emails: '',
    },
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { isDirty, submitCount, errors },
  } = form;
  const onSubmit = (data: any) => {
    const { role, emails } = data;
    if (emails.length) {
      if (emails.length > RemainingCount)
        setError(
          'emails',
          {
            type: 'focus',
            message: `${t('forms.err_msg.user_limit', {
              field: `${limit}`,
            })}`,
          },
          { shouldFocus: true },
        );
      else {
        const payload = emails.map((item: any) => {
          const user = {
            user_role: role,
            email: item.value.trim(),
            weekly_report: false,
            monthly_report: false,
          };
          return user;
        });
        inviteUsers({ account_invites: payload });
        setTimeout(() => {
          toggleDrawer(false);
        }, 500);
      }
    }
  };
  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  React.useEffect(() => {
    setFormValue('emails', selectedEmail);
  }, [selectedEmail]);

  React.useEffect(() => {
    register('role');
  }, []);

  return (
    <FormContainer
      onSubmit={handleSubmit(onSubmit, (err) => console.log('ERR', err))}
    >
      <FormHeader hideBorder>
        <div className="heading">
          {t(`${user.fields?.inviteUserForm?.name}`)}
        </div>
      </FormHeader>
      <FormContent>
        <FormField>
          <Dropdown
            isRequired
            id={'modal-dd-role'}
            label={`${t(`${user.fields?.selectRole?.name}`)}`}
            itemToString={(v: any) => v.name}
            emptyItemsText={`${t('no_results_found')}`}
            itemComponent={(item: any) => (
              <CustomSelect>
                <div className="item-heading">{item.name}</div>
                <div className="item-desc">{item.desc}</div>
              </CustomSelect>
            )}
            items={invite_roles}
            defaultSelectedItem={
              invite_roles.find((r) => watch('role') === r.id) || ''
            }
            onChange={(option: any) => {
              if (!option) return;
              setFormValue('role', option.id);
            }}
            errorText={errors?.role?.message}
            isErrored={Boolean(submitCount > 0 && errors.role?.message)}
          />
        </FormField>
        <FormField
          className={submitCount > 0 && errors?.emails?.message ? 'error' : ''}
        >
          <div className="custom-label">
            {t(`${user.fields?.userEmails?.name}`)}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div className="hint">{t(`${user.fields?.userEmails?.hint}`)}</div>
            <div className="hint">
              <Trans
                i18nKey={`${user.fields?.remaining?.name}`}
                values={{
                  count: RemainingCount,
                }}
                components={{ bold: <b /> }}
              />
            </div>
          </div>

          <MultiSelectDropdown
            id="modal-dd-email"
            name="tags"
            items={[]}
            customValues
            selectedItem={selectedEmail}
            onChange={(newone: any) => {
              setSelectedEmail(newone.filter((item: any) => item.value));
            }}
            itemLabelIdentifier="label"
            itemValueIdentifier="value"
            placeholder={
              selectedEmail.length < RemainingCount
                ? `${t(
                    'settings.user_management.fields.inviteUserForm.invite_more_users',
                  )}`
                : `${t(
                    'settings.user_management.fields.inviteUserForm.users_invite_limit_reached',
                  )}`
            }
          />
          {submitCount > 0 && errors?.emails?.message && (
            <ErrorField>{errors?.emails?.message}</ErrorField>
          )}
        </FormField>
        {/* <FormField>
          <ToggleWrapper className="flex">
            <div className="flex">
              <div className="toggle-label">{user.fields?.weekly?.name}</div>
              <Toggle
                on={!!weeklyDigest}
                hasIcon={false}
                handleChange={() => setWeeklyDigest(!weeklyDigest)}
              />
            </div>
            <div className="flex">
              <div className="toggle-label">{user.fields?.monthly?.name}</div>
              <Toggle
                on={!!monthlyDigest}
                hasIcon={false}
                handleChange={() => setMonthlyDigest(!monthlyDigest)}
              />
            </div>
          </ToggleWrapper>
        </FormField> */}
      </FormContent>
      <FormActions>
        <div style={{ visibility: 'hidden' }}>
          <Button
            inline
            type="secondary"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
            }}
          >
            {t('cancel')}
          </Button>
        </div>
        <div>
          <Button
            inline
            type="secondary"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              toggleDrawer(false);
              setSelectedTab(selectedTab);
            }}
          >
            {t(`${user.fields?.inviteUserForm?.cancelButton}`)}
          </Button>
          <Button
            inline
            type="primary"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              handleSubmit(onSubmit, (err) => console.log('ERR', err))();
            }}
            disabled={!isDirty || selectedEmail.length > RemainingCount}
          >
            {t(`${user.fields?.inviteUserForm?.okButton}`)}
          </Button>
        </div>
      </FormActions>
    </FormContainer>
  );
};

export default NewUserForm;
